/**
 * W.E.E. Guided Access — Public Landing Page
 *
 * Public entry point for clients, volunteers, donors, and partners.
 * No authentication required. All form submissions route to existing
 * Supabase tables via the public API (src/lib/api/public.ts).
 *
 * Staff login remains at /login — this page never touches auth logic.
 */
import React, { useState, useCallback , useEffect} from 'react'
import { Link } from 'react-router-dom'
import ResourceDirectoryContent from '@/components/ResourceDirectoryContent'
import VolunteerRegistrationForm from '@/components/VolunteerRegistrationForm'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import toast, { Toaster } from 'react-hot-toast'
import { supabase } from '@/lib/supabase'
import {
  submitSupportRequest,
  submitDonorContact,
  submitPartnerInquiry,
  submitCommunityPromotionInquiry,
  submitTransportationApplication,
  submitAgencyReferral,
} from '@/lib/api/public'

// ── Brand constants ───────────────────────────────────────────
const BRAND_PINK   = '#d6336c'
const BRAND_DEEP   = '#a61b5e'
const BRAND_GOLD   = '#f0a23a'
const EMAIL        = 'info@womenempoweringeachother.org'

// ── County list ───────────────────────────────────────────────
// W.E.E. primary 14-county Big Bend service area
const WEE_SERVICE_COUNTIES = [
  'Bay',
  'Calhoun',
  'Franklin',
  'Gadsden',
  'Gulf',
  'Holmes',
  'Jackson',
  'Jefferson',
  'Leon',
  'Liberty',
  'Madison',
  'Taylor',
  'Wakulla',
  'Washington',
]

// All 67 Florida counties for families outside the Big Bend region
const ALL_FLORIDA_COUNTIES = [
  'Alachua','Baker','Bay','Bradford','Brevard','Broward','Calhoun',
  'Charlotte','Citrus','Clay','Collier','Columbia','DeSoto','Dixie',
  'Duval','Escambia','Flagler','Franklin','Gadsden','Gilchrist',
  'Glades','Gulf','Hamilton','Hardee','Hendry','Hernando','Highlands',
  'Hillsborough','Holmes','Indian River','Jackson','Jefferson','Lafayette',
  'Lake','Lee','Leon','Levy','Liberty','Madison','Manatee','Marion',
  'Martin','Miami-Dade','Monroe','Nassau','Okaloosa','Okeechobee',
  'Orange','Osceola','Palm Beach','Pasco','Pinellas','Polk','Putnam',
  'Santa Rosa','Sarasota','Seminole','St. Johns','St. Lucie','Sumter',
  'Suwannee','Taylor','Union','Volusia','Wakulla','Walton','Washington',
]

const NEED_OPTIONS = [
  { value: 'diaper_distribution',      label: '🍼 Diapers & Baby Supplies' },
  { value: 'formula_assistance',        label: '🥛 Formula' },
  { value: 'clothing_assistance',       label: '👕 Clothing' },
  { value: 'food_assistance',           label: '🍎 Food Assistance' },
  { value: 'family_stabilization',      label: '🏠 Family Stabilization' },
  { value: 'ess_benefits_navigation',   label: '📋 Benefits Navigation' },
  { value: 'mental_health_referral',    label: '💙 Mental Health Referral' },
  { value: 'housing_referral',          label: '🏘️ Housing Referral' },
  { value: 'employment_referral',       label: '💼 Employment Referral' },
  { value: 'transportation',            label: '🚗 Transportation' },
  { value: 'utility_assistance',        label: '💡 Utility Assistance' },
]

// ══ Reusable field components — MODULE SCOPE ══════════════════
// All at module scope to prevent focus loss on re-render

const FieldInput = React.forwardRef<
  HTMLInputElement,
  { label: string; error?: string; required?: boolean } & React.InputHTMLAttributes<HTMLInputElement>
>(({ label, error, required, ...props }, ref) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}{required && <span className="text-brand-500 ml-0.5">*</span>}
    </label>
    <input
      ref={ref}
      className={`w-full rounded-xl border px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 transition-colors ${error ? 'border-red-400' : 'border-gray-300'}`}
      {...props}
    />
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
))
FieldInput.displayName = 'FieldInput'

const FieldSelect = React.forwardRef<
  HTMLSelectElement,
  { label: string; error?: string; required?: boolean; children: React.ReactNode } & React.SelectHTMLAttributes<HTMLSelectElement>
>(({ label, error, required, children, ...props }, ref) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}{required && <span className="text-brand-500 ml-0.5">*</span>}
    </label>
    <select
      ref={ref}
      className={`w-full rounded-xl border px-4 py-3 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 transition-colors ${error ? 'border-red-400' : 'border-gray-300'}`}
      {...props}
    >
      {children}
    </select>
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
))
FieldSelect.displayName = 'FieldSelect'

const FieldTextarea = React.forwardRef<
  HTMLTextAreaElement,
  { label: string; error?: string; required?: boolean } & React.TextareaHTMLAttributes<HTMLTextAreaElement>
>(({ label, error, required, ...props }, ref) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}{required && <span className="text-brand-500 ml-0.5">*</span>}
    </label>
    <textarea
      ref={ref}
      className={`w-full rounded-xl border px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none transition-colors ${error ? 'border-red-400' : 'border-gray-300'}`}
      {...props}
    />
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
))
FieldTextarea.displayName = 'FieldTextarea'

const PrimaryBtn = React.memo<{ loading?: boolean; children: React.ReactNode } & React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ loading, children, className = '', ...props }) => (
    <button
      className={`inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-2xl font-semibold text-white text-sm shadow-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-500 disabled:opacity-60 ${className}`}
      style={{ background: loading ? '#c2255c' : `linear-gradient(135deg, ${BRAND_PINK}, ${BRAND_DEEP})` }}
      disabled={loading || props.disabled}
      {...props}
    >
      {loading && <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />}
      {children}
    </button>
  )
)
PrimaryBtn.displayName = 'PrimaryBtn'

const OutlineBtn = React.memo<{ children: React.ReactNode } & React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ children, className = '', ...props }) => (
    <button
      className={`inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-2xl font-semibold text-sm border-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${className}`}
      {...props}
    >
      {children}
    </button>
  )
)
OutlineBtn.displayName = 'OutlineBtn'

// ══ Modal wrapper ═════════════════════════════════════════════
const Modal = React.memo<{ open: boolean; onClose: () => void; title: string; children: React.ReactNode }>(
  ({ open, onClose, title, children }) => {
    React.useEffect(() => {
      if (!open) return
      const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
      document.addEventListener('keydown', onKey)
      document.body.style.overflow = 'hidden'
      return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = '' }
    }, [open, onClose])

    if (!open) return null
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} aria-hidden="true" />
        <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100" style={{ background: `linear-gradient(135deg, ${BRAND_PINK}15, ${BRAND_GOLD}10)` }}>
            <h2 id="modal-title" className="text-lg font-bold text-gray-900">{title}</h2>
            <button onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-600 text-2xl leading-none p-1 rounded-lg transition-colors">×</button>
          </div>
          <div className="overflow-y-auto flex-1 px-6 py-5">{children}</div>
        </div>
      </div>
    )
  }
)
Modal.displayName = 'Modal'

// ══ Success card ══════════════════════════════════════════════
const SuccessCard = React.memo<{ title: string; message: string; onClose: () => void }>(
  ({ title, message, onClose }) => (
    <div className="text-center py-6 space-y-4">
      <div className="text-6xl" aria-hidden="true">💛</div>
      <h3 className="text-xl font-bold text-gray-900">{title}</h3>
      <p className="text-gray-600 text-sm leading-relaxed">{message}</p>
      <button onClick={onClose} className="mt-2 px-6 py-2.5 rounded-xl font-semibold text-white text-sm" style={{ background: BRAND_PINK }}>
        Close
      </button>
    </div>
  )
)
SuccessCard.displayName = 'SuccessCard'

// ══ CountySelector — handles Big Bend + all FL + Other ══════
interface CountySelectorProps {
  label?:    string
  required?: boolean
  error?:    string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register:  any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  watch:     any
  fieldName?: string
}

// CountySelector must NOT use React.memo — watch() values must trigger re-renders
const CountySelector: React.FC<CountySelectorProps> = (
  { label = 'Your County', required, error, register, watch, fieldName = 'county' }
) => {
  const selectedCounty         = watch(fieldName) ?? ''
  const isOther                = selectedCounty === '__other__'
  const [showAll, setShowAll]  = React.useState(false)
  const counties               = showAll ? ALL_FLORIDA_COUNTIES : WEE_SERVICE_COUNTIES

  return (
    <div className="space-y-2">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}{required && <span className="text-brand-500 ml-0.5">*</span>}
        </label>
        <select
          {...register(fieldName)}
          className={`w-full rounded-xl border px-4 py-3 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 transition-colors ${error ? 'border-red-400' : 'border-gray-300'}`}
        >
          <option value="">Select your county…</option>
          <optgroup label={showAll ? 'All Florida Counties' : 'W.E.E. Service Area — Big Bend Region'}>
            {counties.map(co => (
              <option key={co} value={co}>{co} County</option>
            ))}
          </optgroup>
          <option value="__other__">My county is not listed…</option>
        </select>
        {error && !isOther && <p className="text-xs text-red-500 mt-1">{error}</p>}
      </div>

      {!showAll && !isOther && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="text-xs font-medium hover:underline"
          style={{ color: '#d6336c' }}
        >
          Not in the Big Bend Region? Show all Florida counties →
        </button>
      )}

      {isOther && (
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">
            Please type your county{required && <span className="text-brand-500 ml-0.5">*</span>}
          </label>
          <input
            {...register(`${fieldName}_other`)}
            type="text"
            autoFocus
            placeholder="e.g. Hillsborough, Orange, Duval…"
            className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
          <p className="text-xs text-gray-500">
            W.E.E. primarily serves the Big Bend Region but a team member will follow up.
          </p>
        </div>
      )}
    </div>
  )
}

// ══ Support Request Form ══════════════════════════════════════
const supportSchema = z.object({
  first_name:     z.string().min(1, 'First name required'),
  last_name:      z.string().min(1, 'Last name required'),
  phone:          z.string().min(7, 'Phone number required'),
  email:          z.string().email().optional().or(z.literal('')),
  county:         z.string().min(1, 'Please select your county'),
  household_size: z.coerce.number().int().min(1).optional(),
  county_other:   z.string().optional(),
  notes:          z.string().optional(),
  priority:       z.string().optional(),
  insurance_status: z.enum(['insured','uninsured','not_sure','prefer_not_to_say']).default('not_sure'),
  payer_name:     z.string().optional(),
  plan_type:      z.string().optional(),
  benefits_navigation_requested: z.boolean().optional(),
  insurance_help_areas: z.array(z.string()).optional(),
  consent_followup: z.boolean().refine(v => v === true, 'Please confirm follow-up consent'),
})
type SupportFormData = z.infer<typeof supportSchema>

const SupportRequestForm = React.memo<{ onSuccess: () => void }>(({ onSuccess }) => {
  const [needs, setNeeds] = useState<string[]>([])
  const [needsError, setNeedsError] = useState('')
  const [insuranceHelpAreas, setInsuranceHelpAreas] = useState<string[]>([])
  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm<SupportFormData>({ resolver: zodResolver(supportSchema) })

  const toggleNeed = useCallback((val: string) => {
    setNeeds(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
    setNeedsError('')
  }, [])

  const onSubmit = useCallback(async (data: SupportFormData) => {
    if (needs.length === 0) { setNeedsError('Please select at least one area of support'); return }
    try {
      const countyValue = data.county === '__other__' ? (data.county_other || 'Other') : data.county
      await submitSupportRequest({
        ...data,
        county: countyValue,
        needs,
        insurance_help_areas: insuranceHelpAreas,
      })
      onSuccess()
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      if (msg.includes('policy') || msg.includes('permission') || msg.includes('violates') || msg.includes('RLS')) {
        toast.error('Submissions temporarily unavailable — please email us at ' + EMAIL, { duration: 8000 })
      } else {
        toast.error('Something went wrong. Please email ' + EMAIL, { duration: 8000 })
        console.error('[W.E.E.]', msg)
      }
    }
  }, [needs, insuranceHelpAreas, onSuccess])

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <div className="text-sm text-gray-600 bg-brand-50 rounded-xl p-3 border border-brand-100 space-y-2">
        <p><strong>Guided Access is a controlled access pathway.</strong> Submitting this request does not guarantee eligibility, an appointment, a resource, a referral, or service.</p>
        <p>Your information is used to review your request, determine the appropriate W.E.E. pathway based on capacity and program requirements, and follow up when appropriate.</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FieldInput label="First Name" required error={errors.first_name?.message} placeholder="Jane" {...register('first_name')} />
        <FieldInput label="Last Name" required error={errors.last_name?.message} placeholder="Smith" {...register('last_name')} />
      </div>
      <FieldInput label="Phone Number" required type="tel" autoComplete="tel" error={errors.phone?.message} placeholder="(850) 555-0000" {...register('phone')} />
      <FieldInput label="Email (optional)" type="email" autoComplete="email" placeholder="you@email.com" {...register('email')} />
      <CountySelector
        required
        error={errors.county?.message}
        register={register}
        watch={watch}
      />
      <FieldInput label="Household Size" type="number" min="1" placeholder="e.g. 4" {...register('household_size')} />
      <div className="rounded-2xl border border-brand-100 bg-brand-50 p-4 space-y-3">
        <div>
          <p className="text-sm font-semibold text-gray-800">Insurance &amp; Benefits Navigation</p>
          <p className="text-xs text-gray-600 mt-1">
            You do not need insurance to ask W.E.E. for help. If you have coverage—or are unsure—we can help you explore whether your plan or other benefit programs may help with some of the needs you selected. Coverage varies by plan and eligibility; W.E.E. does not guarantee payment or coverage.
          </p>
        </div>
        <FieldSelect label="What best describes your current health coverage?" {...register('insurance_status')}>
          <option value="not_sure">I'm not sure</option>
          <option value="insured">I have health insurance</option>
          <option value="uninsured">I do not currently have health insurance</option>
          <option value="prefer_not_to_say">Prefer not to say</option>
        </FieldSelect>
        {watch('insurance_status') === 'insured' && (
          <div className="grid grid-cols-2 gap-3">
            <FieldInput label="Insurance / Payer Name (optional)" placeholder="e.g. Humana" {...register('payer_name')} />
            <FieldInput label="Plan Type (optional)" placeholder="e.g. Medicaid, Medicare, Marketplace, employer plan" {...register('plan_type')} />
          </div>
        )}
        <label className="flex items-start gap-3 rounded-xl bg-white border border-brand-100 p-3 text-xs text-gray-600">
          <input type="checkbox" className="mt-0.5" {...register('benefits_navigation_requested')} />
          <span><strong className="text-gray-700">Yes, help me explore possible coverage or benefit options.</strong> This can include insurance-covered services, transportation or other plan benefits, public coverage/benefit programs, and community resources that may fit my situation.</span>
        </label>
        <div>
          <p className="text-xs font-medium text-gray-700 mb-2">What would you like us to explore? (optional)</p>
          <div className="flex flex-wrap gap-2">
            {[
              ['healthcare','Healthcare / medical services'],
              ['mental_health','Mental health / behavioral health'],
              ['transportation','Transportation to care'],
              ['maternal_child','Pregnancy / postpartum / child services'],
              ['food_nutrition','Food / nutrition support'],
              ['housing_utilities','Housing / utilities support'],
              ['home_supports','Home or community-based supports'],
              ['coverage_navigation','Coverage / benefits enrollment'],
            ].map(([value,label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setInsuranceHelpAreas(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value])}
                className={`px-3 py-2 rounded-xl text-xs font-medium border ${insuranceHelpAreas.includes(value) ? 'text-white border-brand-500' : 'text-gray-600 border-gray-200 bg-white hover:border-brand-300'}`}
                style={insuranceHelpAreas.includes(value) ? { background: BRAND_PINK } : {}}
                aria-pressed={insuranceHelpAreas.includes(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">What do you need support with?<span className="text-brand-500 ml-0.5">*</span></p>
        <div className="flex flex-wrap gap-2">
          {NEED_OPTIONS.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => toggleNeed(opt.value)}
              className={`px-3 py-2 rounded-xl text-xs font-medium transition-all border ${needs.includes(opt.value) ? 'text-white border-brand-500' : 'text-gray-600 border-gray-200 bg-gray-50 hover:border-brand-300'}`}
              style={needs.includes(opt.value) ? { background: BRAND_PINK } : {}}
              aria-pressed={needs.includes(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {needsError && <p className="text-xs text-red-500 mt-1">{needsError}</p>}
      </div>
      <FieldSelect label="If we could help you solve one thing first, what should we prioritize?" {...register('priority')}>
        <option value="">Select a priority…</option>
        <option value="immediate_crisis">Immediate safety or crisis concern</option>
        <option value="housing">Housing / homelessness prevention</option>
        <option value="food">Food / basic needs</option>
        <option value="diapers_baby_supplies">Diapers / baby supplies</option>
        <option value="transportation">Transportation</option>
        <option value="benefits">Benefits / resource navigation</option>
        <option value="healthcare">Healthcare connection</option>
        <option value="other">Other</option>
      </FieldSelect>
      <FieldTextarea label="Anything else you'd like us to know" rows={3} placeholder="What is happening right now, and what would help most?" {...register('notes')} />
      <label className="flex items-start gap-3 rounded-xl bg-gray-50 border border-gray-200 p-3 text-xs text-gray-600">
        <input type="checkbox" className="mt-0.5" {...register('consent_followup')} />
        <span>I authorize W.E.E. to contact me about this request and understand that W.E.E. does not determine eligibility for external programs. Eligibility and service decisions are made by the applicable agency or provider.</span>
      </label>
      {errors.consent_followup && <p className="text-xs text-red-500">{errors.consent_followup.message}</p>}
      <PrimaryBtn type="submit" loading={isSubmitting} className="w-full">
        {isSubmitting ? 'Submitting…' : 'Submit Support Request'}
      </PrimaryBtn>
    </form>
  )
})
SupportRequestForm.displayName = 'SupportRequestForm'

// ══ Volunteer Registration Gateway ════════════════════════════
const VolunteerForm = React.memo(() => {
  const [selectedType, setSelectedType] = useState<'individual' | 'group' | null>(null)
  const registrationRef = React.useRef<HTMLDivElement>(null)

  const chooseType = useCallback((type: 'individual' | 'group') => {
    setSelectedType(type)
    requestAnimationFrame(() => registrationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
  }, [])

  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-brand-50 border border-brand-100 p-4">
        <p className="text-sm font-semibold text-gray-900">W.E.E. Guided Access Volunteer Registration</p>
        <p className="text-sm text-gray-600 mt-1 leading-relaxed">
          Choose the way you are volunteering. The registration form will open right here so you never have to leave this page.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3" role="group" aria-label="Volunteer registration type">
        {([
          ['individual', '🌸', 'Individual Volunteer', 'Register yourself and tell W.E.E. how you would like to help.'],
          ['group', '🤝', 'Group Volunteer', 'Register an organization, team, church, school, or community group.'],
        ] as const).map(([type, icon, title, description]) => (
          <button
            key={type}
            type="button"
            aria-pressed={selectedType === type}
            onClick={() => chooseType(type)}
            className={`text-left rounded-2xl p-5 border-2 transition-colors ${selectedType === type ? 'border-brand-500 bg-brand-50' : type === 'group' ? 'border-orange-200 bg-orange-50 hover:bg-orange-100' : 'border-brand-200 bg-white hover:bg-brand-50'}`}
          >
            <span className="block text-2xl mb-2" aria-hidden="true">{icon}</span>
            <span className="block font-bold text-gray-900">{title}</span>
            <span className="block text-xs text-gray-600 mt-1">{description}</span>
            <span className="block text-sm font-semibold mt-3" style={{ color: type === 'group' ? '#c2410c' : BRAND_PINK }}>
              {selectedType === type ? 'Selected · Continue below ↓' : 'Choose this option →'}
            </span>
          </button>
        ))}
      </div>

      <div className="rounded-2xl bg-gray-50 border border-gray-200 p-4 text-xs text-gray-600 leading-relaxed">
        <p><strong className="text-gray-800">For group visits:</strong> the group contact can register the group first, and participating volunteers can also complete individual registrations so W.E.E. can maintain accurate volunteer records and service-hour history.</p>
      </div>

      {selectedType && (
        <div ref={registrationRef} className="scroll-mt-28 rounded-3xl border-2 border-brand-100 bg-white p-5 md:p-7 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
            <div>
              <p className="text-xs font-bold uppercase tracking-wider" style={{ color: BRAND_PINK }}>Volunteer registration</p>
              <h3 className="text-xl font-bold text-gray-900 mt-1">
                {selectedType === 'group' ? 'Group Volunteer Registration' : 'Individual Volunteer Registration'}
              </h3>
              <p className="text-sm text-gray-500 mt-1">Complete the form below. Your information stays in this Guided Access pathway.</p>
            </div>
            <button type="button" onClick={() => setSelectedType(null)} className="text-sm font-semibold text-gray-500 hover:text-gray-800">
              Hide form
            </button>
          </div>
          <VolunteerRegistrationForm key={selectedType} initialType={selectedType} />
        </div>
      )}
    </div>
  )
})
VolunteerForm.displayName = 'VolunteerForm'

// ══ Donor Form ════════════════════════════════════════════════
const donorSchema = z.object({
  first_name:         z.string().optional(),
  last_name:          z.string().optional(),
  organization_name:  z.string().optional(),
  email:              z.string().email('Valid email required'),
  phone:              z.string().optional(),
  donor_type:         z.string().default('individual'),
  newsletter_opt_in:  z.boolean().default(true),
  notes:              z.string().optional(),
})
type DonorFormData = z.infer<typeof donorSchema>

const DonorForm = React.memo<{ onSuccess: () => void }>(({ onSuccess }) => {
  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm<DonorFormData>({ resolver: zodResolver(donorSchema), defaultValues: { donor_type: 'individual', newsletter_opt_in: true } })
  const donorType = watch('donor_type')
  const isOrg = ['business','church','organization','grantor','sponsor'].includes(donorType)
  const onSubmit = useCallback(async (data: DonorFormData) => {
    try { await submitDonorContact(data); onSuccess() }
    catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      toast.error(msg.includes('policy') ? 'We could not save your donor connection right now. Please try again or email ' + EMAIL : 'Something went wrong. Please email ' + EMAIL, { duration: 8000 })
    }
  }, [onSuccess])
  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-gradient-to-br from-brand-50 to-orange-50 border border-brand-100 p-5">
        <p className="text-sm font-bold text-gray-900">💛 Your giving is part of W.E.E.'s sustainability.</p>
        <p className="text-sm text-gray-600 mt-1 leading-relaxed">Donors are not just funding W.E.E. — you are helping sustain the pathway that keeps families connected to support. Choose the giving option that feels best to you.</p>
      </div>
      <div className="rounded-2xl border border-brand-100 bg-white p-5">
        <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: BRAND_PINK }}>Choose how you'd like to give</p>
        <div className="grid sm:grid-cols-2 gap-2.5">
          <a href="https://donorbox.org/womenempoweringeachother" target="_blank" rel="noopener noreferrer" className="rounded-xl border-2 border-brand-200 bg-brand-50/50 p-4 hover:bg-brand-50 transition-colors">
            <span className="block font-bold text-gray-900">💗 Online Giving — Donorbox</span>
            <span className="block text-xs text-gray-500 mt-1">One-time or recurring gifts.</span>
          </a>
          <a href="https://givelink.app/charities/women-empowering-eachother-inc?utm_source=ci&utm_campaign=women-empowering-eachother-inc&wl=humanitarian-distribution-essentials&utm_medium=copy" target="_blank" rel="noopener noreferrer" className="rounded-xl border-2 border-orange-200 bg-orange-50 p-4 hover:bg-orange-100 transition-colors">
            <span className="block font-bold text-gray-900">🤲🏽 Online Giving — Givelink</span>
            <span className="block text-xs text-gray-600 mt-1">Another online giving route.</span>
          </a>
          <button type="button" onClick={() => document.getElementById('wee-other-giving')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-left rounded-xl border border-gray-200 bg-gray-50 p-4 hover:border-brand-200 transition-colors">
            <span className="block font-bold text-gray-900">📦 In-Kind / Supplies</span>
            <span className="block text-xs text-gray-500 mt-1">Tell W.E.E. what you'd like to donate.</span>
          </button>
          <button type="button" onClick={() => document.getElementById('wee-other-giving')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-left rounded-xl border border-gray-200 bg-gray-50 p-4 hover:border-brand-200 transition-colors">
            <span className="block font-bold text-gray-900">🤝 Sponsorship</span>
            <span className="block text-xs text-gray-500 mt-1">Explore sponsorship and promotional support.</span>
          </button>
          <button type="button" onClick={() => document.getElementById('wee-other-giving')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-left rounded-xl border border-gray-200 bg-gray-50 p-4 hover:border-brand-200 transition-colors">
            <span className="block font-bold text-gray-900">💛 Pledge / Major Gift</span>
            <span className="block text-xs text-gray-500 mt-1">Connect with W.E.E. about a larger or planned gift.</span>
          </button>
          <button type="button" onClick={() => document.getElementById('wee-other-giving')?.scrollIntoView({ behavior: 'smooth', block: 'start' })} className="text-left rounded-xl border border-gray-200 bg-gray-50 p-4 hover:border-brand-200 transition-colors">
            <span className="block font-bold text-gray-900">✨ Another Way to Give</span>
            <span className="block text-xs text-gray-500 mt-1">Tell us what you have in mind.</span>
          </button>
        </div>
      </div>
      <div id="wee-other-giving" className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
        <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Connect With W.E.E. for another giving option</p>
        <p className="text-sm text-gray-600 mb-4">Use this form for an in-kind gift, sponsorship, pledge, major gift, or another way you want to support W.E.E.</p>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <FieldSelect label="I am donating as" {...register('donor_type')}>
            <option value="individual">An Individual</option>
            <option value="business">A Business</option>
            <option value="church">A Church</option>
            <option value="organization">An Organization</option>
            <option value="grantor">A Grant Funder</option>
          </FieldSelect>
          {isOrg && <FieldInput label="Organization Name" {...register('organization_name')} />}
          <div className="grid grid-cols-2 gap-3">
            <FieldInput label="First Name" {...register('first_name')} />
            <FieldInput label="Last Name" {...register('last_name')} />
          </div>
          <FieldInput label="Email" required type="email" error={errors.email?.message} {...register('email')} />
          <FieldInput label="Phone" type="tel" {...register('phone')} />
          <label className="flex items-center gap-2.5 cursor-pointer text-sm text-gray-600 select-none">
            <input type="checkbox" {...register('newsletter_opt_in')} className="w-4 h-4 rounded" style={{ accentColor: BRAND_PINK }} />
            Sign me up for W.E.E. impact updates
          </label>
          <FieldTextarea label="Message (optional)" rows={2} placeholder="I'd like to donate in-kind, pledge monthly, sponsor an event…" {...register('notes')} />
          <PrimaryBtn type="submit" loading={isSubmitting} className="w-full">
            {isSubmitting ? 'Saving…' : 'Connect With W.E.E. for Another Giving Option'}
          </PrimaryBtn>
        </form>
      </div>
    </div>
  )
})
// ══ Partner Form ══════════════════════════════════════════════
const partnerSchema = z.object({
  organization_name: z.string().min(1, 'Organization name required'),
  contact_name:      z.string().min(1, 'Contact name required'),
  email:             z.string().email('Valid email required'),
  phone:             z.string().optional(),
  agency_type:       z.string().optional(),
  county:            z.string().optional(),
  county_other:      z.string().optional(),
  notes:             z.string().optional(),
})
type PartnerFormData = z.infer<typeof partnerSchema>

const PartnerForm = React.memo<{ onSuccess: () => void }>(({ onSuccess }) => {
  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm<PartnerFormData>({ resolver: zodResolver(partnerSchema) })
  const onSubmit = useCallback(async (data: PartnerFormData) => {
    try { await submitPartnerInquiry(data); onSuccess() }
    catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      if (msg.includes('policy') || msg.includes('permission') || msg.includes('violates')) {
        toast.error('Submissions are not yet enabled. Please email us directly at ' + EMAIL, { duration: 8000 })
      } else {
        toast.error('Something went wrong. Please email ' + EMAIL + ' — Error: ' + msg, { duration: 8000 })
      }
    }
  }, [onSuccess])
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <p className="text-sm text-gray-600 bg-brand-50 rounded-xl p-3 border border-brand-100">
        We welcome partnerships with agencies, churches, businesses, and community organizations serving Big Bend families.
      </p>
      <FieldInput label="Organization Name" required error={errors.organization_name?.message} {...register('organization_name')} />
      <FieldInput label="Primary Contact Name" required error={errors.contact_name?.message} {...register('contact_name')} />
      <FieldInput label="Email" required type="email" error={errors.email?.message} {...register('email')} />
      <FieldInput label="Phone" type="tel" {...register('phone')} />
      <FieldSelect label="Organization Type" {...register('agency_type')}>
        <option value="">Select type…</option>
        <option value="food_bank">Food Bank</option>
        <option value="shelter">Shelter / Housing</option>
        <option value="church">Faith Community</option>
        <option value="school">School / Early Learning</option>
        <option value="health">Health / Medical</option>
        <option value="government">Government Agency</option>
        <option value="nonprofit">Other Nonprofit</option>
        <option value="business">Business</option>
        <option value="other">Other</option>
      </FieldSelect>
      <CountySelector label="County Served" register={register} watch={watch} />
      <FieldTextarea label="How would you like to partner with W.E.E.?" required rows={3} placeholder="Describe your organization's mission and how you'd like to collaborate…" {...register('notes')} />
      <PrimaryBtn type="submit" loading={isSubmitting} className="w-full">
        {isSubmitting ? 'Submitting…' : 'Submit Partnership Inquiry'}
      </PrimaryBtn>
    </form>
  )
})
PartnerForm.displayName = 'PartnerForm'

// ══ Service Card ══════════════════════════════════════════════
const ServiceCard = React.memo<{ emoji: string; title: string; description: string; onClick: () => void; cta: string }>(
  ({ emoji, title, description, onClick, cta }) => (
    <div className="bg-white rounded-3xl p-6 shadow-soft border border-gray-100 flex flex-col gap-4 hover:shadow-lg transition-shadow duration-300">
      <div className="text-4xl" aria-hidden="true">{emoji}</div>
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-1">{title}</h3>
        <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
      </div>
      <button
        onClick={onClick}
        className="mt-auto text-sm font-semibold transition-colors focus:outline-none focus:underline"
        style={{ color: BRAND_PINK }}
        aria-label={`${cta} — ${title}`}
      >
        {cta} →
      </button>
    </div>
  )
)
ServiceCard.displayName = 'ServiceCard'

// ══ Stat bubble ══════════════════════════════════════════════
const StatBubble = React.memo<{ value: string; label: string }>(({ value, label }) => (
  <div className="text-center">
    <p className="text-3xl md:text-4xl font-bold" style={{ color: BRAND_GOLD }}>{value}</p>
    <p className="text-sm text-white/80 mt-0.5">{label}</p>
  </div>
))
StatBubble.displayName = 'StatBubble'


// ══ TransportationForm — MODULE SCOPE ════════════════════════
const transportSchema = z.object({
  first_name:      z.string().min(1, 'Required'),
  last_name:       z.string().min(1, 'Required'),
  phone:           z.string().min(7, 'Required'),
  email:           z.string().email().optional().or(z.literal('')),
  county:          z.string().min(1, 'Required'),
  address:         z.string().optional(),
  reason:          z.string().min(10, 'Please describe your transportation need'),
  frequency:       z.string().optional(),
  household_size:  z.coerce.number().int().min(1).optional(),
  county_other:    z.string().optional(),
  notes:           z.string().optional(),
})
type TransportForm = z.infer<typeof transportSchema>

const TRANSPORT_REASONS = [
  'Medical appointments',
  'WIC / SNAP office visits',
  'Social services appointments',
  'W.E.E. diaper pickup',
  'Job interviews / employment',
  'School / childcare',
  'Other essential travel',
]

const TransportationForm = React.memo<{ onSuccess: () => void }>(({ onSuccess }) => {
  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm<TransportForm>({
    resolver: zodResolver(transportSchema),
  })
  const onSubmit = useCallback(async (data: TransportForm) => {
    try {
      const countyVal = data.county === '__other__' ? (data.county_other || 'Other') : data.county
      await submitTransportationApplication({ ...data, county: countyVal })
      onSuccess()
    } catch (err) {
      const msg = err instanceof Error ? err.message : ''
      toast.error(msg.includes('policy') ? 'Please email us at ' + EMAIL : 'Something went wrong — please email ' + EMAIL, { duration: 8000 })
    }
  }, [onSuccess])
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <p className="text-sm text-gray-600 bg-blue-50 rounded-xl p-3 border border-blue-100">
        This form is for clients who cannot access W.E.E. services due to lack of transportation. A team member will follow up within 1–2 business days.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <FieldInput label="First Name" required error={errors.first_name?.message} {...register('first_name')} />
        <FieldInput label="Last Name" required error={errors.last_name?.message} {...register('last_name')} />
      </div>
      <FieldInput label="Phone" required type="tel" error={errors.phone?.message} {...register('phone')} />
      <FieldInput label="Email (optional)" type="email" {...register('email')} />
      <FieldInput label="Street Address" placeholder="Where would you need to be picked up?" {...register('address')} />
      <CountySelector required error={errors.county?.message} register={register} watch={watch} />
      <FieldInput label="Household Size" type="number" min="1" {...register('household_size')} />
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">Reason for transportation need <span className="text-brand-500">*</span></p>
        <div className="flex flex-wrap gap-2">
          {TRANSPORT_REASONS.map(r => (
            <label key={r} className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <input type="radio" value={r} {...register('reason')} className="accent-brand-500" />
              {r}
            </label>
          ))}
        </div>
        {errors.reason && <p className="text-xs text-red-500 mt-1">{errors.reason.message}</p>}
      </div>
      <FieldSelect label="How often do you need transportation?" {...register('frequency')}>
        <option value="">Select frequency…</option>
        <option value="one_time">One time only</option>
        <option value="weekly">Weekly</option>
        <option value="bi_weekly">Every two weeks</option>
        <option value="monthly">Monthly</option>
        <option value="as_needed">As needed</option>
      </FieldSelect>
      <FieldTextarea label="Additional details" rows={2} placeholder="Any other information that would help us assist you…" {...register('notes')} />
      <PrimaryBtn type="submit" loading={isSubmitting} className="w-full">
        {isSubmitting ? 'Submitting…' : 'Submit Transportation Application'}
      </PrimaryBtn>
    </form>
  )
})
TransportationForm.displayName = 'TransportationForm'

// ══ AgencyReferralForm — MODULE SCOPE ════════════════════════
const referralSchema = z.object({
  agency_name:     z.string().min(1, 'Agency name required'),
  agency_contact:  z.string().min(1, 'Contact name required'),
  agency_email:    z.string().email('Valid email required'),
  agency_phone:    z.string().optional(),
  client_first:    z.string().min(1, 'Required'),
  client_last:     z.string().min(1, 'Required'),
  client_phone:    z.string().optional(),
  client_county:   z.string().min(1, 'Required'),
  client_county_other: z.string().optional(),
  estimated_families: z.coerce.number().int().min(0).optional(),
  projected_diaper_packs: z.coerce.number().int().min(0).optional(),
  projected_wipes_packs: z.coerce.number().int().min(0).optional(),
  projected_formula_cans: z.coerce.number().int().min(0).optional(),
  projected_food_boxes: z.coerce.number().int().min(0).optional(),
  projected_hygiene_kits: z.coerce.number().int().min(0).optional(),
  projected_clothing_sets: z.coerce.number().int().min(0).optional(),
  urgency:         z.string().default('standard'),
  notes:           z.string().optional(),
})
type ReferralForm = z.infer<typeof referralSchema>

const REFERRAL_SERVICES = [
  { value: 'diaper_distribution', label: '🍼 Diapers & Supplies' },
  { value: 'formula_assistance',   label: '🥛 Formula' },
  { value: 'family_stabilization', label: '🏠 Family Stabilization' },
  { value: 'ess_benefits_navigation', label: '📋 Benefits Navigation' },
  { value: 'mental_health_referral',  label: '💙 Mental Health' },
  { value: 'transportation',          label: '🚗 Transportation' },
  { value: 'clothing_assistance',     label: '👕 Clothing' },
  { value: 'food_assistance',         label: '🍎 Food Assistance' },
]

const AgencyReferralForm = React.memo<{ onSuccess: () => void }>(({ onSuccess }) => {
  const [services, setServices] = useState<string[]>([])
  const [servicesError, setServicesError] = useState('')
  const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm<ReferralForm>({
    resolver: zodResolver(referralSchema),
    defaultValues: { urgency: 'standard' },
  })
  const toggleService = useCallback((val: string) => {
    setServices(p => p.includes(val) ? p.filter(v => v !== val) : [...p, val])
    setServicesError('')
  }, [])
  const onSubmit = useCallback(async (data: ReferralForm) => {
    if (!services.length) { setServicesError('Select at least one service'); return }
    try {
      const countyVal = data.client_county === '__other__' ? (data.client_county_other || 'Other') : data.client_county
      await submitAgencyReferral({ ...data, client_county: countyVal, services_needed: services })
      onSuccess()
    } catch (err) {
      const msg = err instanceof Error ? err.message : ''
      toast.error('Guided Access could not submit this request. Please try again.', { duration: 8000 })
    }
  }, [services, onSuccess])
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <p className="text-sm text-gray-600 bg-amber-50 rounded-xl p-3 border border-amber-100">
        All agency and organization requests for W.E.E. support must now begin through W.E.E. Guided Access. This form replaces email-based referrals and notices. Submit the request here so W.E.E. can review the request, current capacity, program requirements, and the appropriate next step. Submission does not guarantee acceptance, service, or referral fulfillment.
      </p>
      <div className="border-t border-gray-100 pt-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Your Agency</p>
        <div className="space-y-3">
          <FieldInput label="Agency / Organization Name" required error={errors.agency_name?.message} {...register('agency_name')} />
          <div className="grid grid-cols-2 gap-3">
            <FieldInput label="Your Name" required error={errors.agency_contact?.message} {...register('agency_contact')} />
            <FieldInput label="Your Phone" type="tel" {...register('agency_phone')} />
          </div>
          <FieldInput label="Your Email" required type="email" error={errors.agency_email?.message} {...register('agency_email')} />
        </div>
      </div>
      <div className="border-t border-gray-100 pt-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Community Member / Service Context</p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <FieldInput label="Client First Name" required error={errors.client_first?.message} {...register('client_first')} />
            <FieldInput label="Client Last Name" required error={errors.client_last?.message} {...register('client_last')} />
          </div>
          <FieldInput label="Client Phone" type="tel" {...register('client_phone')} />
          <CountySelector
        label="Client's County"
        required
        error={errors.client_county?.message}
        register={register}
        watch={watch}
        fieldName="client_county"
      />
        </div>
      </div>
      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">Services Needed <span className="text-brand-500">*</span></p>
        <div className="flex flex-wrap gap-2">
          {REFERRAL_SERVICES.map(s => (
            <button key={s.value} type="button" onClick={() => toggleService(s.value)}
              className={`px-3 py-1.5 rounded-xl text-xs font-medium transition-all border ${services.includes(s.value) ? 'text-white border-brand-500' : 'text-gray-600 border-gray-200 bg-gray-50'}`}
              style={services.includes(s.value) ? { background: BRAND_PINK } : {}} aria-pressed={services.includes(s.value)}>
              {s.label}
            </button>
          ))}
        </div>
        {servicesError && <p className="text-xs text-red-500 mt-1">{servicesError}</p>}
      </div>

      <div className="rounded-2xl border border-orange-200 bg-orange-50 p-4 space-y-4">
        <div>
          <p className="text-sm font-bold text-gray-900">📦 Projected Supply Request</p>
          <p className="text-xs text-gray-600 mt-1">For agency-wide or disaster-response requests, estimate how many families you expect to serve and the quantities needed. Enter 0 or leave an item blank if it is not needed. These are requested estimates, not a guarantee of fulfillment.</p>
        </div>
        <FieldInput label="Estimated families / households to support" type="number" min="0" placeholder="e.g. 75" {...register('estimated_families')} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {services.includes('diaper_distribution') && <>
            <FieldInput label="Diaper packs / cases requested" type="number" min="0" placeholder="e.g. 40 packs" {...register('projected_diaper_packs')} />
            <FieldInput label="Wipes packs / cases requested" type="number" min="0" placeholder="e.g. 30 packs" {...register('projected_wipes_packs')} />
          </>}
          {services.includes('formula_assistance') && <FieldInput label="Formula cans requested" type="number" min="0" placeholder="e.g. 60 cans" {...register('projected_formula_cans')} />}
          {services.includes('food_assistance') && <FieldInput label="Food boxes / kits requested" type="number" min="0" placeholder="e.g. 50 boxes" {...register('projected_food_boxes')} />}
          {services.includes('clothing_assistance') && <FieldInput label="Clothing sets requested" type="number" min="0" placeholder="e.g. 25 sets" {...register('projected_clothing_sets')} />}
          {(services.includes('diaper_distribution') || services.includes('clothing_assistance') || services.includes('food_assistance')) && <FieldInput label="Hygiene / essential kits requested" type="number" min="0" placeholder="e.g. 40 kits" {...register('projected_hygiene_kits')} />}
        </div>
        {services.length === 0 && <p className="text-xs text-gray-500">Choose one or more services above to see the relevant supply quantity fields.</p>}
      </div>

      <FieldSelect label="Urgency" {...register('urgency')}>
        <option value="standard">Standard (1–2 business days)</option>
        <option value="urgent">Urgent (same day if possible)</option>
        <option value="crisis">Crisis (immediate attention needed)</option>
      </FieldSelect>
      <FieldTextarea label="Additional context for W.E.E. staff" rows={3} placeholder="Relevant background, barriers, special considerations, or anything the W.E.E. team should know when reviewing this request…" {...register('notes')} />
      <PrimaryBtn type="submit" loading={isSubmitting} className="w-full">
        {isSubmitting ? 'Submitting…' : 'Submit Access Inquiry'}
      </PrimaryBtn>
    </form>
  )
})
AgencyReferralForm.displayName = 'AgencyReferralForm'

// ══ DocumentUploadForm — MODULE SCOPE ════════════════════════
const docUploadSchema = z.object({
  first_name:  z.string().min(1, 'Required'),
  last_name:   z.string().min(1, 'Required'),
  phone:       z.string().optional(),
  email:       z.string().email().optional().or(z.literal('')),
  doc_type:    z.string().min(1, 'Select document type'),
  notes:       z.string().optional(),
})
type DocUploadForm = z.infer<typeof docUploadSchema>

const DOC_TYPES = [
  { value: 'id',           label: 'Government ID' },
  { value: 'proof_income', label: 'Proof of Income' },
  { value: 'proof_address',label: 'Proof of Address' },
  { value: 'birth_cert',   label: 'Birth Certificate' },
  { value: 'medical',      label: 'Medical Documentation' },
  { value: 'insurance',    label: 'Insurance Card' },
  { value: 'other',        label: 'Other Document' },
]

const DocumentUploadForm = React.memo<{ onSuccess: () => void }>(({ onSuccess }) => {
  const [file,      setFile]      = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [fileError, setFileError] = useState('')
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm<DocUploadForm>({
    resolver: zodResolver(docUploadSchema),
  })
  const handleFile = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (!f) return
    const ext = f.name.split('.').pop()?.toLowerCase() || ''
    const allowedExts = new Set(['pdf','jpg','jpeg','png','webp','heic','heif','doc','docx'])
    if (!allowedExts.has(ext)) {
      setFile(null)
      setFileError('Unsupported file type. Please use PDF, JPG, PNG, WEBP, HEIC, HEIF, DOC, or DOCX.')
      return
    }
    if (f.size > 10 * 1024 * 1024) { setFile(null); setFileError('File must be under 10MB'); return }
    setFile(f); setFileError('')
  }, [])

  const onSubmit = useCallback(async (data: DocUploadForm) => {
    if (!file) { setFileError('Please select a file to upload'); return }
    setUploading(true)
    try {
      const rawExt = file.name.split('.').pop()?.toLowerCase() || 'bin'
      const safeExt = rawExt.replace(/[^a-z0-9]/g, '') || 'bin'
      const safeLastName = data.last_name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'client'
      const storagePath = `public/${crypto.randomUUID()}-${safeLastName}-${data.doc_type}.${safeExt}`
      const mimeByExt: Record<string, string> = {
        heic: 'image/heic',
        heif: 'image/heif',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        webp: 'image/webp',
        pdf: 'application/pdf',
        doc: 'application/msword',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      }
      const detectedType = file.type?.toLowerCase() || ''
      const allowedMimeTypes = new Set(Object.values(mimeByExt))
      const contentType = allowedMimeTypes.has(detectedType)
        ? detectedType
        : (mimeByExt[safeExt] || 'application/octet-stream')

      const { error: uploadErr } = await supabase.storage
        .from('client-documents')
        .upload(storagePath, file, { upsert: false, contentType })

      if (uploadErr) {
        const detail = uploadErr.message.toLowerCase()
        if (detail.includes('mime') || detail.includes('type')) {
          throw new Error('This file type is not supported. Please use PDF, JPG, PNG, HEIC, or HEIF.')
        }
        if (detail.includes('size')) {
          throw new Error('This file is too large. Please choose a file under 10MB.')
        }
        if (detail.includes('mime') || detail.includes('type')) {
          throw new Error('This file type is not accepted by the secure document service. Please use PDF, JPG, PNG, WEBP, HEIC, HEIF, DOC, or DOCX.')
        }
        throw new Error('The secure document service could not accept this file right now. Please try again with a different file.')
      }

      const notes = [
        'SECURE DOCUMENT UPLOAD',
        `Document type: ${data.doc_type}`,
        `File: ${file.name}`,
        `Storage path: ${storagePath}`,
        data.notes?.trim() ? `Notes: ${data.notes.trim()}` : null,
      ].filter(Boolean).join('\n')

      const { error: inquiryErr } = await supabase.from('outreach_inquiries').insert({
        first_name: data.first_name.trim(),
        last_name:  data.last_name.trim(),
        phone:      data.phone?.trim() || null,
        email:      data.email?.trim() || null,
        notes,
        status:     'new',
      })

      if (inquiryErr) {
        // Do not leave a client document orphaned in private storage if the
        // matching staff intake notice cannot be created.
        await supabase.storage.from('client-documents').remove([storagePath])
        throw new Error('Your document could not be matched to a W.E.E. intake record. The upload was rolled back. Please try again or contact ' + EMAIL + '.')
      }

      onSuccess()
    } catch (err) {
      const msg = err instanceof Error ? err.message : ''
      toast.error(msg || 'Upload failed. Please email documents to ' + EMAIL, { duration: 10000 })
    } finally { setUploading(false) }
  }, [file, onSuccess])
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <p className="text-sm text-gray-600 bg-green-50 rounded-xl p-3 border border-green-100">
        🔒 Documents are securely stored and only accessible to authorized W.E.E. staff. Files are encrypted and never shared externally.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <FieldInput label="First Name" required error={errors.first_name?.message} {...register('first_name')} />
        <FieldInput label="Last Name" required error={errors.last_name?.message} {...register('last_name')} />
      </div>
      <FieldInput label="Phone" type="tel" {...register('phone')} />
      <FieldInput label="Email" type="email" {...register('email')} />
      <FieldSelect label="Document Type" required error={errors.doc_type?.message} {...register('doc_type')}>
        <option value="">Select document type…</option>
        {DOC_TYPES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
      </FieldSelect>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Select File <span className="text-brand-500">*</span>
          <span className="text-gray-400 font-normal ml-1">(PDF, JPG, PNG, HEIC, HEIF — max 10MB)</span>
        </label>
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,.doc,.docx"
          onChange={handleFile}
          aria-label="Select document file"
          className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-medium file:text-white file:cursor-pointer cursor-pointer"
          style={{ '--file-btn-bg': BRAND_PINK } as React.CSSProperties}
        />
        {file && <p className="text-xs text-green-600 mt-1">✓ {file.name} ({(file.size / 1024).toFixed(0)} KB)</p>}
        {fileError && <p className="text-xs text-red-500 mt-1">{fileError}</p>}
      </div>
      <FieldTextarea label="Notes (optional)" rows={2} placeholder="What is this document for? Any context for staff…" {...register('notes')} />
      <PrimaryBtn type="submit" loading={isSubmitting || uploading} className="w-full">
        {uploading ? 'Uploading securely…' : isSubmitting ? 'Submitting…' : '🔒 Upload Document Securely'}
      </PrimaryBtn>
    </form>
  )
})
DocumentUploadForm.displayName = 'DocumentUploadForm'

// ══ PUBLIC LANDING PAGE ═══════════════════════════════════════

const CommunityPromotionForm = React.memo<{ onSuccess: () => void }>(({ onSuccess }) => {
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(z.object({
      organization_name: z.string().min(1, 'Organization or business name required'),
      contact_name: z.string().min(1, 'Contact name required'),
      email: z.string().email('Valid email required'),
      phone: z.string().optional(),
      website: z.string().optional(),
      promotion_type: z.string().min(1, 'Please select a promotion type'),
      message: z.string().optional(),
    }))
  })
  const onSubmit = useCallback(async (data: any) => {
    try { await submitCommunityPromotionInquiry(data); onSuccess() }
    catch (err) {
      toast.error(err instanceof Error ? err.message : 'Unable to submit the inquiry right now.', { duration: 8000 })
    }
  }, [onSuccess])
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <p className="text-sm text-gray-600 bg-brand-50 rounded-xl p-3 border border-brand-100">
        Interested in a paid promotional placement on W.E.E. Guided Access? Submit an inquiry and our team will review availability, audience fit, placement options, pricing, and terms.
      </p>
      <FieldInput label="Organization / Business Name" required error={errors.organization_name?.message as string | undefined} {...register('organization_name')} />
      <FieldInput label="Primary Contact Name" required error={errors.contact_name?.message as string | undefined} {...register('contact_name')} />
      <FieldInput label="Email" required type="email" error={errors.email?.message as string | undefined} {...register('email')} />
      <FieldInput label="Phone" type="tel" {...register('phone')} />
      <FieldInput label="Website" type="url" placeholder="https://..." {...register('website')} />
      <FieldSelect label="Promotion Interest" required error={errors.promotion_type?.message as string | undefined} {...register('promotion_type')}>
        <option value="">Select an option…</option>
        <option value="community_partner_spotlight">Community Partner Spotlight</option>
        <option value="website_promotional_placement">Website Promotional Placement</option>
        <option value="sponsored_campaign">Sponsored Campaign / Feature</option>
        <option value="other">Other / Not Sure Yet</option>
      </FieldSelect>
      <FieldTextarea label="Tell us about what you would like to promote" rows={3} {...register('message')} />
      <PrimaryBtn type="submit" loading={isSubmitting} className="w-full">
        {isSubmitting ? 'Submitting…' : 'Submit Promotion Inquiry'}
      </PrimaryBtn>
    </form>
  )
})
CommunityPromotionForm.displayName = 'CommunityPromotionForm'

type ActiveModal = 'support' | 'volunteer' | 'donor' | 'partner' | 'transportation' | 'referral' | 'documents' | 'promotion' | null
type PublicTab = 'home' | 'community' | 'help' | 'agency' | 'volunteer' | 'resources' | 'partner' | 'about'


const PublicLandingPage: React.FC = () => {
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }) }, [])

  const [activeModal, setActiveModal] = useState<ActiveModal>(null)
  const [successModal, setSuccessModal] = useState<{ title: string; message: string } | null>(null)
  const [activeTab, setActiveTab] = useState<PublicTab>('home')
  const [agencyMode, setAgencyMode] = useState<'client' | 'partnership'>('client')

  const open = useCallback((m: ActiveModal) => {
    if (['support', 'transportation', 'referral', 'documents'].includes(m ?? '')) {
      setSuccessModal({
        title: 'Temporary Intake Pause',
        message: 'W.E.E. has temporarily reached its current service capacity and is working through existing inquiries. New service intake and supporting-document submissions are paused for now. Please follow our page for updates when intake reopens. If you need immediate emergency assistance, call 911; for community resource navigation, contact 211.'
      })
      return
    }
    setActiveModal(m)
  }, [])
  const close = useCallback(() => setActiveModal(null), [])
  const handleSuccess = useCallback((title: string, message: string) => {
    setActiveModal(null)
    setSuccessModal({ title, message })
  }, [])

  const tabs: Array<{ id: PublicTab; label: string; icon: string }> = [
    { id: 'home', label: 'Home & Updates', icon: '🏠' },
    { id: 'community', label: 'Community Hub', icon: '📸' },
    { id: 'help', label: 'Get Help', icon: '🌸' },
    { id: 'agency', label: 'Agency / Organization', icon: '📋' },
    { id: 'volunteer', label: 'Volunteer', icon: '🙋🏽‍♀️' },
    { id: 'resources', label: 'Resources', icon: '🔎' },
    { id: 'partner', label: 'Partner', icon: '🤝' },
  ]

  const go = useCallback((tab: PublicTab) => {
    setActiveTab(tab)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900">
      <Toaster position="top-center" toastOptions={{ style: { borderRadius: '12px', fontFamily: 'Inter, sans-serif' } }} />

      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-white px-4 py-2 rounded-lg text-brand-500 font-semibold z-50">
        Skip to main content
      </a>

      {/* ── Primary navigation ───────────────────────────── */}
      <nav className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm" role="navigation" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-3 sm:px-5">
          <div className="flex items-center justify-between min-h-16 py-2 gap-3">
            <button type="button" onClick={() => go('home')} className="flex items-center gap-3 text-left shrink-0">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-md" style={{ background: `linear-gradient(135deg, ${BRAND_PINK}, ${BRAND_DEEP})` }} aria-hidden="true">W</div>
              <div>
                <p className="font-bold text-gray-900 text-sm leading-tight">W.E.E. Guided Access™</p>
                <p className="text-xs text-gray-500 hidden lg:block">Women Empowering Each Other, Inc.</p>
              </div>
            </button>
            <div className="flex items-center gap-1 overflow-x-auto max-w-full" role="tablist" aria-label="W.E.E. public pathways">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  onClick={() => go(tab.id)}
                  className={`shrink-0 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold transition-all border ${activeTab === tab.id ? 'text-white border-transparent shadow-sm' : 'text-gray-600 border-transparent hover:bg-gray-50 hover:text-gray-900'}`}
                  style={activeTab === tab.id ? { background: BRAND_PINK } : {}}
                >
                  <span className="mr-1" aria-hidden="true">{tab.icon}</span>{tab.label}
                </button>
              ))}
              <Link to="/dare" className="shrink-0 rounded-full px-3 py-2 text-xs font-bold text-white shadow-sm" style={{ background: '#d6336c' }} aria-label="D.A.RE. Disaster Assistance and Recovery">🚨 D.A.RE.</Link>
              <Link to="/login" className="shrink-0 ml-1 text-xs text-gray-400 hover:text-gray-600 px-2" aria-label="Staff login">Staff Login</Link>
            </div>
          </div>
        </div>
      </nav>

      <section aria-labelledby="capacity-pause-heading" className="border-b border-amber-200 bg-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-start gap-3">
            <span className="text-xl" aria-hidden="true">💗</span>
            <div>
              <h2 id="capacity-pause-heading" className="font-bold text-amber-950">Temporary Intake Pause</h2>
              <p className="mt-1 text-sm leading-relaxed text-amber-900">W.E.E. has temporarily reached its current service capacity and is working through existing inquiries. New service intake forms are paused while we catch up. We will share updates when intake reopens. Thank you for your patience and understanding.</p>
            </div>
          </div>
        </div>
      </section>

            <style>{`
        @keyframes weeMarquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .wee-marquee-track {
          animation: weeMarquee 24s linear infinite;
        }
        .wee-marquee-track:hover {
          animation-play-state: paused;
        }
        @media (prefers-reduced-motion: reduce) {
          .wee-marquee-track { animation: none; }
        }
      `}</style>

<main id="main-content">
        {activeTab === 'home' && (
          <>
            {/* Home is intentionally the updates/notice board first. */}
            <section className="relative overflow-hidden" style={{ background: `linear-gradient(145deg, ${BRAND_DEEP} 0%, ${BRAND_PINK} 55%, #f45699 100%)` }}>
              <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10 bg-white translate-x-32 -translate-y-32" aria-hidden="true" />
              <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full opacity-10 bg-white -translate-x-20 translate-y-20" aria-hidden="true" />
              <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20">
                <div className="max-w-3xl">
                  <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-1.5 mb-5">
                    <span aria-hidden="true">📢</span>
                    <span className="text-white text-xs font-semibold uppercase tracking-wider">W.E.E. Community Updates</span>
                  </div>
                  <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white leading-tight mb-4">Welcome to W.E.E. Guided Access™</h1>
                  <p className="text-xl md:text-2xl text-white/90 font-light leading-snug max-w-2xl">A coordinated pathway to resources, support, and community connection.</p>
                  <p className="text-white/75 mt-4 max-w-2xl leading-relaxed">New visitors can explore W.E.E. from here. Returning visitors can skip directly to the pathway they need using the tabs above.</p>
                </div>
              </div>
            </section>

            <section className="py-7 bg-rose-50 border-b border-rose-100">
              <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="rounded-2xl border border-rose-200 bg-white p-5 md:flex md:items-center md:justify-between md:gap-6">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-brand-700 mb-1">🚨 W.E.E. D.A.RE. — Disaster Assistance & Recovery Efforts</p>
                    <h2 className="font-bold text-gray-900 text-lg">Preparing now for storm needs and the disasters still ahead.</h2>
                    <p className="text-sm text-gray-600 mt-1">Families can request assistance. Organizations can offer response capacity. Donors and community supporters can help W.E.E. prepare essential resources before needs peak.</p>
                  </div>
                  <Link to="/dare" className="mt-4 md:mt-0 shrink-0 px-5 py-3 rounded-xl text-sm font-semibold text-white" style={{ background: BRAND_PINK }}>Open D.A.RE. →</Link>
                </div>
              </div>
            </section>

            <section className="py-8 bg-amber-50 border-b border-amber-100">
              <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="rounded-2xl border border-amber-200 bg-white/85 p-5 md:flex md:items-center md:justify-between md:gap-6">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-1">📢 Current Guided Access Notice</p>
                    <h2 className="font-bold text-gray-900 text-lg">Guided Access is the starting point for coordinated W.E.E. services.</h2>
                    <p className="text-sm text-gray-600 mt-1">Complete the appropriate intake first. W.E.E. reviews the request and determines the appropriate next step based on capacity, program requirements, geography, and available resources.</p>
                  </div>
                  <button onClick={() => go('help')} className="mt-4 md:mt-0 shrink-0 px-5 py-3 rounded-xl text-sm font-semibold text-white" style={{ background: BRAND_PINK }}>Begin Guided Access →</button>
                </div>
              </div>
            </section>

            <section className="py-12 bg-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="rounded-3xl border border-amber-200 bg-amber-50 p-7 md:p-9">
                  <p className="text-xs font-bold uppercase tracking-wider mb-2 text-amber-700">📸 W.E.E. Community Hub</p>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Community media is under consideration.</h2>
                  <p className="mt-3 text-gray-600 leading-relaxed max-w-3xl">
                    We’re reviewing the Community Hub media presentation before making it publicly available again.
                    The rest of W.E.E. Guided Access remains available, including help requests, resources, volunteering,
                    partnerships, donations, and secure document submission.
                  </p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <Link to="/resources" className="inline-flex items-center justify-center rounded-2xl px-5 py-3 font-semibold border-2 border-gray-200 bg-white text-gray-700">Community Resources →</Link>
                    <button type="button" onClick={() => open('donor')} className="inline-flex items-center justify-center rounded-2xl px-5 py-3 font-semibold text-white" style={{ background: BRAND_PINK }}>Support W.E.E. →</button>
                  </div>
                </div>
              </div>
            </section>

            <section className="py-12 bg-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="grid lg:grid-cols-[1.25fr_.75fr] gap-6">
                  <div className="rounded-3xl border border-brand-100 bg-gradient-to-br from-brand-50 to-orange-50 p-7 md:p-9">
                    <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: BRAND_PINK }}>✨ What's New</p>
                    <h2 className="text-2xl md:text-3xl font-bold mb-3">Growing With Our Community</h2>
                    <p className="text-gray-600 leading-relaxed mb-5">New notices, announcements, community activity, program highlights, events, partner spotlights, media, and impact stories can appear here as the W.E.E. Community Hub grows.</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {[
                        ['📣','Announcements','Important changes and W.E.E. notices.'],
                        ['📅','Upcoming Events','Workshops, distributions, and community activities.'],
                        ['📸','Community Media','Photos, videos, and program highlights.'],
                        ['❤️','Impact & Stories','Outcomes, milestones, and community voices.'],
                      ].map(([icon,title,desc]) => (
                        <div key={title} className="rounded-2xl bg-white/80 border border-white p-4">
                          <div className="text-2xl mb-2" aria-hidden="true">{icon}</div>
                          <p className="font-bold text-sm">{title}</p>
                          <p className="text-xs text-gray-600 mt-1">{desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-3xl border border-gray-100 bg-gray-50 p-7">
                    <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: BRAND_DEEP }}>Start Here</p>
                    <h2 className="text-2xl font-bold mb-3">What brings you to W.E.E. today?</h2>
                    <div className="space-y-2">
                      {[
                        ['🌸','I need help','help'],
                        ['📋','I am an agency / organization','agency'],
                        ['🙋🏽‍♀️','I want to volunteer','volunteer'],
                        ['🔎','I need community resources','resources'],
                        ['🤝','I want to partner with W.E.E.','partner'],
                      ].map(([icon,label,id]) => (
                        <button key={id} onClick={() => go(id as PublicTab)} className="w-full text-left rounded-2xl bg-white border border-gray-200 p-4 hover:border-brand-200 hover:shadow-sm transition-all">
                          <span className="mr-2" aria-hidden="true">{icon}</span><span className="font-semibold text-sm">{label}</span><span className="float-right text-gray-400">→</span>
                        </button>
                      ))}
                      <Link to="/dare#support-dare" className="block w-full text-left rounded-2xl border-2 border-rose-200 bg-rose-50 p-4 hover:shadow-md transition-all">
                        <span className="mr-2" aria-hidden="true">🚨</span><span className="font-semibold text-sm">D.A.RE. disaster preparedness & support</span><span className="float-right text-brand-500">→</span>
                        <span className="block text-xs text-gray-500 mt-2 ml-7">Need help, want to donate, or represent an organization that can help.</span>
                      </Link>
                      <button onClick={() => open('donor')} className="w-full text-left rounded-2xl border-2 border-brand-200 bg-gradient-to-br from-brand-50 to-orange-50 p-4 hover:shadow-md transition-all">
                        <span className="mr-2" aria-hidden="true">💛</span><span className="font-semibold text-sm">I only want to donate</span><span className="float-right text-brand-500">→</span>
                        <span className="block text-xs text-gray-500 mt-2 ml-7">Give online or choose another way to support W.E.E.</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="py-10 bg-white border-b border-gray-100">
              <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="rounded-3xl border border-brand-100 bg-gradient-to-r from-brand-50 via-white to-orange-50 p-6 md:p-8 shadow-sm">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: BRAND_PINK }}>💛 Support W.E.E.</p>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Only here to give? Start right here.</h2>
                      <p className="text-gray-600 mt-2 max-w-2xl leading-relaxed">You do not need to complete a Guided Access intake to support W.E.E. Choose the giving path that works best for you — online giving, in-kind support, sponsorship, a pledge or major gift, or another way to give.</p>
                    </div>
                    <button onClick={() => open('donor')} className="shrink-0 inline-flex items-center justify-center px-7 py-3.5 rounded-2xl font-semibold text-white shadow-lg" style={{ background: BRAND_PINK }}>
                      💛 Give to W.E.E. →
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <section className="py-10 bg-gray-50 border-b border-gray-100">
              <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="rounded-3xl overflow-hidden border border-gray-200 bg-gray-900 text-white shadow-lg">
                  <div className="relative overflow-hidden py-5">
                    <div className="wee-marquee-track flex w-max gap-10 whitespace-nowrap text-sm md:text-base font-bold uppercase tracking-[0.18em]">
                      <span>Women Empowering Eachother, Inc.</span><span aria-hidden="true">✦</span>
                      <span>WE(E)mpower Advocacy</span><span aria-hidden="true">✦</span>
                      <span>WEE Diaper Bank</span><span aria-hidden="true">✦</span>
                      <span>Women Empowering Eachother, Inc.</span><span aria-hidden="true">✦</span>
                      <span>WE(E)mpower Advocacy</span><span aria-hidden="true">✦</span>
                      <span>WEE Diaper Bank</span>
                    </div>
                  </div>
                  <div className="px-6 py-4 bg-white text-gray-700 text-sm md:flex md:items-center md:justify-between md:gap-5">
                    <div>
                      <p className="font-bold">W.E.E. Community Promotion</p>
                      <p className="text-gray-500 mt-1">Paid promotional and advertising opportunities are presented separately from W.E.E.'s current community partners. Approved organizations and businesses can inquire about promotional placement.</p>
                    </div>
                    <button type="button" onClick={() => open('promotion')} className="mt-3 md:mt-0 inline-flex rounded-full bg-brand-50 px-3 py-2 text-xs font-semibold hover:bg-brand-100 transition-colors" style={{ color: BRAND_PINK }}>Community Promotion Inquiry →</button>
                  </div>
                </div>
              </div>
            </section>

            <section className="py-10 bg-white border-b border-gray-100">
              <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="mb-6">
                  <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: BRAND_PINK }}>🤝 Current Community Partners</p>
                  <h2 className="text-2xl md:text-3xl font-bold">Partners helping W.E.E. move resources into the community.</h2>
                  <p className="text-gray-600 mt-2 max-w-3xl">These are W.E.E.'s current community partners and should not be confused with paid promotional or advertising placements.</p>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  {[
                    ['Amazon','Community Partner','Supporting W.E.E. through community resource support.'],
                    ['Baby2Baby','Community Partner','Helping W.E.E. keep essential supplies moving to families.'],
                    ['CARE','Community Partner','Supporting W.E.E. with CARE PACKAGE hygiene resources and emergency-preparedness support.'],
                  ].map(([name,label,desc]) => (
                    <div key={name} className="rounded-3xl border border-gray-100 bg-gray-50 p-6">
                      <p className="text-xs font-bold uppercase tracking-wider" style={{ color: BRAND_PINK }}>{label}</p>
                      <h3 className="mt-2 text-xl font-bold text-gray-900">{name}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-gray-600">{desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section id="community-hub" className="py-10 bg-white">
              <div className="max-w-6xl mx-auto px-4 sm:px-6">
                <div className="rounded-3xl border border-amber-200 bg-amber-50 p-7 md:p-9">
                  <p className="text-xs font-bold uppercase tracking-wider mb-2 text-amber-700">📸 Community Hub</p>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Community media is under consideration.</h2>
                  <p className="mt-2 text-gray-600 leading-relaxed max-w-3xl">W.E.E. is reviewing the public media presentation before restoring photo stories. The rest of Guided Access remains available, including community resources, support requests, agency access, volunteer registration, and donor support.</p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <button type="button" onClick={() => go('community')} className="inline-flex items-center justify-center rounded-2xl px-5 py-3 font-semibold text-white" style={{ background: BRAND_PINK }}>Open Community Hub →</button>
                    <button type="button" onClick={() => open('donor')} className="inline-flex items-center justify-center rounded-2xl px-5 py-3 font-semibold border-2 border-gray-200 bg-white text-gray-700">Support W.E.E. →</button>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}

        {activeTab === 'community' && (
          <section className="py-12 md:py-16 bg-white">
            <div className="max-w-5xl mx-auto px-4 sm:px-6">
              <div className="rounded-3xl border border-amber-200 bg-amber-50 p-8 md:p-10">
                <p className="text-xs font-bold uppercase tracking-wider mb-2 text-amber-700">📸 Community Hub</p>
                <h1 className="text-3xl md:text-5xl font-bold text-gray-900">Media is currently under consideration.</h1>
                <p className="mt-4 text-gray-600 text-lg leading-relaxed max-w-3xl">W.E.E. is reviewing the public media presentation before restoring photo stories here. This intentional pause prevents visitors from seeing incomplete or unreliable media.</p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <button type="button" onClick={() => go('home')} className="inline-flex items-center justify-center rounded-2xl px-6 py-3.5 font-semibold text-white" style={{ background: BRAND_PINK }}>← Back to Guided Access</button>
                  <Link to="/resources" className="inline-flex items-center justify-center rounded-2xl px-6 py-3.5 font-semibold border-2 border-gray-200 bg-white text-gray-700">Community Resources →</Link>
                </div>
              </div>
            </div>
          </section>
        )}

        {activeTab === 'help' && (
          <section className="py-12 md:py-16 bg-white">
            <div className="max-w-5xl mx-auto px-4 sm:px-6">
              <div className="mb-8">
                <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: BRAND_PINK }}>Guided Access</p>
                <h1 className="text-3xl md:text-5xl font-bold mb-3">Get Help</h1>
                <p className="text-gray-600 text-lg max-w-3xl">Tell W.E.E. what is happening and what your household needs. This intake is the starting point for coordinated support.</p>
              </div>
              <div className="rounded-3xl border border-brand-100 bg-gradient-to-br from-brand-50 to-orange-50 p-5 mb-8 text-sm text-gray-700">
                <strong>Important:</strong> Completing an intake does not guarantee eligibility, an appointment, a resource, a referral, or service. W.E.E. reviews requests based on capacity, documented need, program requirements, safety, geography, and available resources.
              </div>
              <div className="mb-6 rounded-3xl border-2 border-brand-200 bg-gradient-to-r from-brand-50 via-white to-orange-50 p-5 md:p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider" style={{ color: BRAND_PINK }}>🔒 Need to send documents?</p>
                    <h2 className="mt-1 text-lg md:text-xl font-bold text-gray-900">Submit supporting documents securely</h2>
                    <p className="mt-1 text-sm text-gray-600 max-w-2xl">If your request requires an ID, proof of income, proof of address, medical documentation, or another supporting document, you can upload it securely here.</p>
                  </div>
                  <button type="button" onClick={() => open('documents')} className="shrink-0 inline-flex items-center justify-center rounded-2xl px-5 py-3 font-semibold text-white shadow-md" style={{ background: BRAND_PINK }}>Upload Documents →</button>
                </div>
              </div>
              <div className="rounded-3xl border-2 border-amber-200 bg-amber-50 p-6 md:p-8" role="status">
                <p className="text-xs font-bold uppercase tracking-wider text-amber-800">Temporary Intake Pause</p>
                <h2 className="mt-2 text-2xl font-bold text-gray-900">We have temporarily reached our current capacity.</h2>
                <p className="mt-3 text-sm leading-relaxed text-gray-700">Our team is working through inquiries already received. New service requests and supporting-document uploads are paused for now. We will provide updates when intake reopens.</p>
                <p className="mt-3 text-sm text-gray-700">You can still browse community resources below or contact <a className="font-bold underline" href="https://211bigbend.org/" target="_blank" rel="noreferrer">211 Big Bend</a> for community resource navigation. For immediate danger, call 911.</p>
              </div>
              <div className="mt-5 rounded-2xl border-2 border-pink-200 bg-pink-50 p-5">
                <p className="text-xs font-bold uppercase tracking-wider text-pink-800">Agency &amp; Doula Pickup · October 29</p>
                <h3 className="mt-1 text-lg font-bold text-gray-900">Request crisis resource pickup</h3>
                <p className="mt-1 text-sm text-gray-700">Small agencies and doulas can request diapers, hygiene products, baby wipes, infant formula, breastfeeding resources, and Frida postpartum pads for Thursday, October 29, 2026, from 1:00–4:00 PM.</p>
                <Link to="/agency-pickup" className="mt-4 inline-flex rounded-xl px-5 py-3 font-bold text-white shadow-sm" style={{ background: BRAND_PINK }}>Open Agency Pickup Intake →</Link>
              </div>
              <div className="mt-6 grid sm:grid-cols-3 gap-3">
                <button onClick={() => go('agency')} className="rounded-2xl border border-gray-200 p-4 text-left hover:border-brand-200"><span className="text-xl">📋</span><p className="font-bold text-sm mt-2">Submitting for someone else?</p><p className="text-xs text-gray-500">Use the Agency / Organization tab.</p></button>
                <Link to="/resources" className="rounded-2xl border border-gray-200 p-4 hover:border-brand-200"><span className="text-xl">🔎</span><p className="font-bold text-sm mt-2">Browse resources</p><p className="text-xs text-gray-500">Explore the public resource directory.</p></Link>
                <button onClick={() => open('transportation')} className="rounded-2xl border border-gray-200 p-4 text-left hover:border-brand-200"><span className="text-xl">🚗</span><p className="font-bold text-sm mt-2">Transportation</p><p className="text-xs text-gray-500">Open the transportation application.</p></button>
              </div>
            </div>
          </section>
        )}

        {activeTab === 'agency' && (
          <section className="py-12 md:py-16 bg-gray-50">
            <div className="max-w-5xl mx-auto px-4 sm:px-6">
              <div className="mb-8">
                <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: BRAND_PINK }}>For Agencies & Organizations</p>
                <h1 className="text-3xl md:text-5xl font-bold mb-3">Agency / Organization Access</h1>
                <p className="text-gray-600 text-lg max-w-3xl">Use this pathway when you are contacting W.E.E. on behalf of a client or community member who may need W.E.E. support.</p>
              </div>
              <div className="mb-6" role="group" aria-label="Agency pathway selection">
                {(['client','partnership'] as const).map(mode => (
                  <button
                    key={mode}
                    type="button"
                    aria-pressed={agencyMode === mode}
                    onClick={() => setAgencyMode(mode)}
                    className={`rounded-xl px-4 py-2.5 mr-2 mb-2 text-sm font-semibold border-2 transition-colors ${agencyMode === mode ? 'text-white border-transparent' : 'text-gray-600 border-gray-200 bg-white hover:border-brand-200'}`}
                    style={agencyMode === mode ? { background: mode === 'client' ? BRAND_PINK : BRAND_GOLD } : {}}
                  >
                    {mode === 'client' ? '📋 Client Request' : '🤝 Partnership'}
                  </button>
                ))}
              </div>

              {agencyMode === 'client' ? (
                <div className="rounded-3xl border border-gray-100 bg-white shadow-soft p-6 md:p-9">
                  <p className="text-sm text-gray-600 mb-5">Submit a request for a specific client through W.E.E. Guided Access. This replaces email-based agency referrals and referral notices.</p>
                  <div className="rounded-2xl border-2 border-amber-200 bg-amber-50 p-5" role="status"><h2 className="font-bold text-gray-900">Temporary Intake Pause</h2><p className="mt-2 text-sm leading-relaxed text-gray-700">W.E.E. has temporarily reached its current service capacity and is working through existing inquiries. New client referral submissions are paused until we announce that intake has reopened. Thank you for helping us give current requests the attention they deserve.</p></div>
                </div>
              ) : (
                <div className="rounded-3xl border border-gray-100 bg-white shadow-soft p-6 md:p-9">
                  <p className="text-sm text-gray-600 mb-5">Use this pathway for an ongoing relationship with W.E.E., including community collaboration, resource pathways, and approved partnership activities.</p>
                  <PartnerForm onSuccess={() => handleSuccess('Inquiry Received!', 'Thank you for your interest in partnering with W.E.E. A team member will review your inquiry and follow up regarding next steps.')} />
                </div>
              )}
            </div>
          </section>
        )}

        {activeTab === 'volunteer' && (
          <section className="py-12 md:py-16 bg-white">
            <div className="max-w-5xl mx-auto px-4 sm:px-6">
              <div className="mb-8">
                <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: BRAND_PINK }}>Join the W.E.E. Community</p>
                <h1 className="text-3xl md:text-5xl font-bold mb-3">Volunteer With Us</h1>
                <p className="text-gray-600 text-lg max-w-3xl">Choose the registration pathway that fits how you want to serve.</p>
              </div>
              <div className="rounded-3xl border border-gray-100 shadow-soft p-6 md:p-9"><VolunteerForm /></div>
            </div>
          </section>
        )}

        {activeTab === 'resources' && (
          <section className="py-12 md:py-16 bg-gray-50">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <div className="mb-8">
                <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: BRAND_PINK }}>Community Hub</p>
                <h1 className="text-3xl md:text-5xl font-bold mb-3">Community Resources</h1>
                <p className="text-gray-600 text-lg max-w-3xl">Browse available community resources for the whole family, then use Guided Access when you need personalized navigation, direct assistance, or coordinated W.E.E. support.</p>
              </div>
              <div className="rounded-3xl bg-white border border-gray-100 shadow-soft p-6 md:p-8">
                <div className="mb-6">
                  <p className="text-sm text-gray-600 max-w-3xl">Browse verified community resources directly here. Organizations can also submit their information for W.E.E. review and consideration for inclusion in the network.</p>
                </div>
                <ResourceDirectoryContent />
              </div>
            </div>
          </section>
        )}

        {activeTab === 'partner' && (
          <section className="py-12 md:py-16 bg-gray-50">
            <div className="max-w-5xl mx-auto px-4 sm:px-6">
              <div className="mb-8">
                <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: BRAND_GOLD }}>For Community Organizations</p>
                <h1 className="text-3xl md:text-5xl font-bold mb-3">Partner With W.E.E.</h1>
                <p className="text-gray-600 text-lg max-w-3xl">Explore an ongoing relationship with W.E.E. for community collaboration, resource pathways, and approved partnership activities.</p>
              </div>
              <div className="rounded-3xl border border-gray-100 bg-white shadow-soft p-6 md:p-9">
                <PartnerForm onSuccess={() => handleSuccess('Inquiry Received!', 'Thank you for your interest in partnering with W.E.E. A team member will review your inquiry and follow up regarding next steps.')} />
              </div>
              <div className="mt-6 rounded-2xl bg-white border border-gray-200 p-5">
                <p className="font-bold">Already a W.E.E. partner?</p>
                <p className="text-sm text-gray-600 mt-1 mb-3">Use your designated portal access.</p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/partner-login" className="inline-flex px-5 py-2.5 rounded-xl border-2 border-brand-200 text-brand-600 text-sm font-semibold">Partner Portal Login →</Link>
                  <Link to="/login" className="inline-flex px-5 py-2.5 rounded-xl border-2 border-gray-200 text-gray-600 text-sm font-semibold">Staff Login →</Link>
                </div>
              </div>
            </div>
          </section>
        )}


        {activeTab === 'about' && (
          <section className="py-12 md:py-16 bg-white">
            <div className="max-w-6xl mx-auto px-4 sm:px-6">
              <div className="mb-10">
                <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: BRAND_PINK }}>Learn About W.E.E.</p>
                <h1 className="text-3xl md:text-5xl font-bold mb-3">How W.E.E. Guided Access Works</h1>
                <p className="text-gray-600 text-lg max-w-3xl">New visitors are welcome to explore the full W.E.E. pathway here. Returning visitors can use the tabs above to go directly where they need to go.</p>
              </div>
              <div className="grid md:grid-cols-5 gap-3">
                {[
                  ['01','Need','Tell us what is happening and what support is needed.','🌸'],
                  ['02','Access','Complete the appropriate Guided Access intake.','🧭'],
                  ['03','Coordination','Our team reviews the request and identifies the appropriate pathway.','🤝'],
                  ['04','Resource / Service','W.E.E. may provide, coordinate, navigate, or refer based on the request and available capacity.','📦'],
                  ['05','Outcome → Evidence','W.E.E. documents the outcome and the support or connection provided.','✓'],
                ].map(([step,title,desc,icon]) => (
                  <div key={step} className="rounded-2xl border border-gray-100 bg-gray-50 p-5 shadow-sm">
                    <div className="flex items-center justify-between mb-4"><span className="text-xs font-bold text-gray-400">{step}</span><span className="text-2xl" aria-hidden="true">{icon}</span></div>
                    <h3 className="font-bold mb-2">{title}</h3><p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
                  </div>
                ))}
              </div>
              <div className="grid md:grid-cols-2 gap-5 mt-8">
                <div className="rounded-3xl border border-brand-100 bg-brand-50 p-7">
                  <h2 className="text-2xl font-bold mb-3">Who We Serve</h2>
                  <p className="text-gray-600 leading-relaxed">W.E.E. is an all-community assistance organization supporting the entire family and community — children, adults, caregivers, women, men, young parents, agencies, organizations, volunteers, donors, and community partners — through coordinated pathways across the Big Bend region.</p>
                  <p className="text-sm font-semibold mt-4" style={{ color: BRAND_PINK }}>14+ counties and counting · 501(c)(3) nonprofit</p>
                </div>
                <div className="rounded-3xl border border-gray-100 bg-gray-50 p-7">
                  <h2 className="text-2xl font-bold mb-3">Explore More</h2>
                  <div className="space-y-2">
                    <button onClick={() => open('documents')} className="w-full text-left rounded-xl bg-white border border-gray-200 p-3 text-sm font-semibold">🔒 Secure Document Upload →</button>
                    <button onClick={() => open('donor')} className="w-full text-left rounded-xl bg-white border border-gray-200 p-3 text-sm font-semibold">💛 Support W.E.E. →</button>
                    <Link to="/resources" className="block rounded-xl bg-white border border-gray-200 p-3 text-sm font-semibold">🔎 Community Resource Directory →</Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      <footer className="bg-gray-900 text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-3 gap-8">
          <div>
            <p className="font-bold">W.E.E. Guided Access™</p>
            <p className="text-gray-400 text-sm mt-2 leading-relaxed">Women Empowering Each Other, Inc. · EIN 47-3183168 · 501(c)(3)</p>
          </div>
          <div>
            <p className="font-semibold text-sm text-gray-300 mb-3">Quick Links</p>
            <div className="flex flex-wrap gap-2">
              {tabs.filter(t => t.id !== 'home').map(tab => <button key={tab.id} onClick={() => go(tab.id)} className="text-gray-400 hover:text-white text-sm">{tab.icon} {tab.label}</button>)}
              <button onClick={() => go('about')} className="text-gray-400 hover:text-white text-sm">💗 About W.E.E.</button>
            </div>
          </div>
          <div>
            <p className="font-semibold text-sm text-gray-300 mb-3">Contact</p>
            <a href={`mailto:${EMAIL}`} className="text-gray-400 hover:text-white text-sm">{EMAIL}</a>
            <p className="text-gray-500 text-xs mt-3">Serving the Big Bend Region of Florida</p>
            <Link to="/login" className="block text-xs text-gray-600 hover:text-gray-400 mt-5">Staff Portal Login →</Link>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 border-t border-gray-800 mt-8 pt-6 text-center text-gray-600 text-xs">
          © {new Date().getFullYear()} Women Empowering Each Other, Inc. All rights reserved. · W.E.E. Guided Access™
        </div>
      </footer>

      {/* ── Secondary modals remain available for focused actions ── */}
      <Modal open={activeModal === 'donor'} onClose={close} title="💛 Give to W.E.E.">
        <DonorForm onSuccess={() => handleSuccess('Thank You!', "We've received your information and will follow up soon with the next steps for your chosen giving path. Your generosity helps sustain W.E.E.'s work across the community.")} />
      </Modal>

      <Modal open={activeModal === 'transportation'} onClose={close} title="🚗 Transportation Assistance Application">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5"><h2 className="font-bold text-gray-900">Temporary Intake Pause</h2><p className="mt-2 text-sm text-gray-700">W.E.E. has temporarily reached its current service capacity and is working through existing inquiries. New transportation applications are paused. Please follow our page for updates when intake reopens.</p></div>
      </Modal>

      <Modal open={activeModal === 'promotion'} onClose={close} title="📣 Community Promotion Inquiry">
        <CommunityPromotionForm onSuccess={() => handleSuccess('Inquiry Received!', 'Thank you for your interest in W.E.E. community promotion. Our team will review your inquiry and follow up regarding availability, placement options, pricing, and terms.')} />
      </Modal>

      <Modal open={activeModal === 'documents'} onClose={close} title="🔒 Secure Document Upload">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5"><h2 className="font-bold text-gray-900">Temporary Intake Pause</h2><p className="mt-2 text-sm text-gray-700">Supporting-document uploads for new service requests are temporarily paused while W.E.E. catches up with current inquiries. Please wait for our update before submitting documents.</p></div>
      </Modal>

      <Modal open={!!successModal} onClose={() => setSuccessModal(null)} title="✅ Submitted">
        {successModal && <SuccessCard title={successModal.title} message={successModal.message} onClose={() => setSuccessModal(null)} />}
      </Modal>
    </div>
  )
}

export default PublicLandingPage
